import { invalidateCacheTags } from "helpers/requestCache"

const objectIdRouteSegmentPattern = /^([a-f0-9]{24})(?:-.+)?$/i
const objectIdPattern = /^[a-f0-9]{24}$/i

const compactUnique = tags => [...new Set(tags.filter(Boolean))]

export const normalizeEventCacheId = eventId => {
  if (!eventId) return null

  const segment = String(eventId)
    .trim()
    .split("/")
    .filter(Boolean)
    .pop()

  const match = segment && segment.match(objectIdRouteSegmentPattern)
  return match ? match[1].toLowerCase() : null
}

export const normalizeObjectId = id => {
  if (!id) return null

  const value = String(id).trim()
  return objectIdPattern.test(value) ? value.toLowerCase() : null
}

const tagWithEventId = (prefix, eventId) => {
  const normalizedEventId = normalizeEventCacheId(eventId)
  return normalizedEventId ? `${prefix}:${normalizedEventId}` : null
}

const tagWithObjectId = (prefix, id) => {
  const normalizedId = normalizeObjectId(id)
  return normalizedId ? `${prefix}:${normalizedId}` : null
}

export const eventCacheTags = {
  all: "events",
  lists: "events:list",
  locations: "events:locations",
  event: eventId => tagWithEventId("event", eventId),
  detail: eventId => tagWithEventId("event-detail", eventId),
  shell: eventId => tagWithEventId("event-shell", eventId),
  competitions: eventId => tagWithEventId("event-competitions", eventId),
  entryCounts: eventId => tagWithEventId("event-entry-counts", eventId),
  competition: competitionId => tagWithObjectId("competition", competitionId),
  competitionDetail: competitionId =>
    tagWithObjectId("competition-detail", competitionId),
  competitionEntries: competitionId =>
    tagWithObjectId("competition-entries", competitionId),
  competitionLists: competitionId =>
    tagWithObjectId("competition-lists", competitionId),
  competitionDraws: competitionId =>
    tagWithObjectId("competition-draws", competitionId),
  competitionMatches: competitionId =>
    tagWithObjectId("competition-matches", competitionId),
  competitionLogs: competitionId =>
    tagWithObjectId("competition-logs", competitionId),
  competitionPoints: competitionId =>
    tagWithObjectId("competition-points", competitionId),
  competitionPenalties: competitionId =>
    tagWithObjectId("competition-penalties", competitionId),
}

const getEventInvalidationTags = eventId => {
  const normalizedEventId = normalizeEventCacheId(eventId)

  if (!normalizedEventId) return null

  return compactUnique([
    eventCacheTags.lists,
    eventCacheTags.locations,
    eventCacheTags.event(normalizedEventId),
    eventCacheTags.detail(normalizedEventId),
    eventCacheTags.shell(normalizedEventId),
    eventCacheTags.competitions(normalizedEventId),
    eventCacheTags.entryCounts(normalizedEventId),
  ])
}

const getCompetitionInvalidationTags = competitionId => {
  const normalizedCompetitionId = normalizeObjectId(competitionId)

  if (!normalizedCompetitionId) return []

  return compactUnique([
    eventCacheTags.competition(normalizedCompetitionId),
    eventCacheTags.competitionDetail(normalizedCompetitionId),
    eventCacheTags.competitionEntries(normalizedCompetitionId),
    eventCacheTags.competitionLists(normalizedCompetitionId),
    eventCacheTags.competitionDraws(normalizedCompetitionId),
    eventCacheTags.competitionMatches(normalizedCompetitionId),
    eventCacheTags.competitionLogs(normalizedCompetitionId),
    eventCacheTags.competitionPoints(normalizedCompetitionId),
    eventCacheTags.competitionPenalties(normalizedCompetitionId),
  ])
}

export const invalidateAllEventCache = () => {
  invalidateCacheTags([eventCacheTags.all])
}

export const invalidateEventCache = ({ eventId, competitionId } = {}) => {
  const eventTags = getEventInvalidationTags(eventId)

  if (!eventTags) {
    invalidateAllEventCache()
    return
  }

  invalidateCacheTags(
    compactUnique([
      ...eventTags,
      ...getCompetitionInvalidationTags(competitionId),
    ])
  )
}

export const invalidateEventCacheAfterSuccess = (response, identity) => {
  if (response) {
    invalidateEventCache(identity)
  }
}

export const createEventsResponseCacheHandler = listenFunction => payload => {
  invalidateAllEventCache()
  return listenFunction(payload)
}
